/** Minimum width/height when resizing shapes (canvas px). */
export const SHAPE_MIN_DIMENSION = 0.01
/** Minimum width/height when resizing text boxes (canvas px). */
/** Matches text-tool create minimum at 100% zoom (`TEXT_BOX_MIN_DIMENSION_SCREEN_PX`). */
export const TEXT_MIN_DIMENSION = 10

// Zoom-invariant: rendered as constant screen pixels via a counter-scale transform
// (`--chrome-inv-scale` = 1 / current zoom), not canvas/doc px. See `selection-chrome.tsx`.
export const HANDLE_SIZE = 8
export const HANDLE_BORDER_WIDTH = 1
export const HIT_AREA_SIZE = 16
export const CENTER_ADJUSTMENT = (HIT_AREA_SIZE - HANDLE_SIZE) / 2

/**
 * Minimum chrome bounding-box size in screen pixels, per axis.
 * Twice HIT_AREA_SIZE so two handle hit targets never overlap on a tiny object.
 */
export const MIN_CHROME_SIZE_PX = 2 * HIT_AREA_SIZE

/** Screen px offset a handle's own anchor sits from the frame edge/corner. */
export const HANDLE_OFFSET = 4
export const ROTATE_CONNECTOR_WIDTH = HANDLE_BORDER_WIDTH
export const ROTATION_CENTER_SIZE = 6
export const ROTATION_CENTER_COLOR = 'var(--editor-bounding-box-origin, #a5a39a)'

/**
 * Dash pattern (SVG `stroke-dasharray`, screen px via `vector-effect: non-scaling-stroke`) for
 * the dashed multi-select frame border. Chosen to visually match the previous CSS `border-style:
 * dashed` look at HANDLE_BORDER_WIDTH — SVG dasharray is authored explicitly rather than derived
 * from the UA's border-style algorithm.
 */
export const SELECTION_DASH_ARRAY = '4 3'

export const SELECTION_COLOR = 'var(--editor-bounding-box-accent, #0284C7)'
export const LOCKED_SELECTION_COLOR = 'var(--color-page-editor-lock, #9333EA)'
/** Cyan outline for frozen-only selection (matches `--color-page-editor-freeze` / #0891B2). */
export const FROZEN_SELECTION_COLOR = 'var(--color-page-editor-freeze, #0891B2)'

/** Below this canvas delta, a move interaction is treated as a click (no grid/guide snap on release). */
export const MOVE_CLICK_THRESHOLD_PX = 3

const ROTATE_CURSOR_URL = '/icons/rotate-handle-cursor.svg'

export const ROTATE_CURSOR = `url("${ROTATE_CURSOR_URL}") 12 12, grab`
export const LOCKED_OBJECT_CURSOR = "url('/icons/locked-object-cursor.svg') 5 5, not-allowed"

export {
  FROZEN_OBJECT_CURSOR,
  GEOMETRY_BLOCKED_PULSE_CLASS,
  GEOMETRY_BLOCKED_PULSE_MS,
} from '@/components/app/editor/selection-chrome/geometry-blocked-feedback'

/** Marks lock/unfreeze controls on selection chrome (skip selection-box drag / click capture). */
export const SELECTION_STATE_BADGE_ATTR = 'data-selection-state-badge'

/** @deprecated Use {@link SELECTION_STATE_BADGE_ATTR}. */
export const SELECTION_UNLOCK_BADGE_ATTR = SELECTION_STATE_BADGE_ATTR

export function isSelectionStateBadgeTarget(target: EventTarget | null): boolean {
  return (
    typeof Element !== 'undefined' &&
    target instanceof Element &&
    target.closest(`[${SELECTION_STATE_BADGE_ATTR}]`) != null
  )
}

/** @deprecated Use {@link isSelectionStateBadgeTarget}. */
export const isSelectionUnlockBadgeTarget = isSelectionStateBadgeTarget
